import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Home } from "lucide-react";
import Footer from "../../components/landing/Footer";
import HeroSlider from "../../components/media_and_events/HeroSlider";
import ConferenceFilters from "../../components/media_and_events/ConferenceFilters";
import ConferenceCard from "../../components/media_and_events/ConferenceCard";
import WhyAttendCarousel from "../../components/media_and_events/WhyAttendCarousel";
import NewsletterSubscription from "../../components/media_and_events/NewsletterSubscription";
import Images from "../../utils/Images";
import eventsService from "../../api/EventsService";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../store/hooks";

export default function EventsListing() {
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const {isAuthenticated} = useAppSelector((s) => s.auth);

  const heroImages = [Images.hero_img_web_three, Images.hero_img_web_four];

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const response = await eventsService.getEvents();
        if (response.data?.status_code === 200 && response?.data?.data) {
          setEvents(response?.data?.data || []);
        } else {
          toast.error(response?.data?.reason || "Failed to load events");
          setEvents([]);
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.reason ||
            error?.message ||
            "An error occurred while fetching events"
        );
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const availableFilters = useMemo(() => {
    const types = new Set();
    events.forEach((item) => {
      (item.event_type || []).forEach((type) => {
        if (type) types.add(type.toLowerCase());
      });
    });
    return ["all", ...Array.from(types)];
  }, [events]);

  const filteredEvents = useMemo(() => {
    let filtered = [...events];

    if (selectedFilter !== "all") {
      filtered = filtered.filter((item) => {
        const eventTypes = item.event_type || [];
        return eventTypes.some(
          (type) => type?.toLowerCase() === selectedFilter
        );
      });
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((item) => {
        const title = (item.event_title || "").toLowerCase();
        const description = (item.about_event || "").toLowerCase();
        const venue = (item.venue_name || "").toLowerCase();
        const city = (item.city || "").toLowerCase();
        const country = (item.country || "").toLowerCase();
        return (
          title.includes(query) ||
          description.includes(query) ||
          venue.includes(query) ||
          city.includes(query) ||
          country.includes(query)
        );
      });
    }

    return filtered;
  }, [events, selectedFilter, searchQuery]);

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <HeroSlider images={heroImages} showBackButton={false} backLink="/media-events" backLabel="Back to Media & Events" />

      {/* Content Section */}
      <section className="px-4 py-12 bg-gradient-to-b from-gray-50 to-white sm:px-6 lg:px-8">
        <div className="mx-auto max-w-8xl">
          <div className="flex gap-4 justify-between items-center mb-8">
            <div className="min-w-0">
              <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl">
                Events
              </h2>
              <div className="mt-2 w-24 h-1 bg-emerald-500 rounded" />
            </div>
            <motion.button
              onClick={() => navigate("/media-events")}
              whileHover={{ scale: 1.03, y: -1 }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex gap-2 items-center px-4 py-2 text-sm font-semibold text-emerald-700 bg-emerald-50 rounded-lg border border-emerald-200 shadow-sm transition-colors duration-200 hover:bg-emerald-100 hover:border-emerald-300"
            >
              <Home size={16} />
              Back to Main Menu
            </motion.button>
          </div>

          {/* Filters and Search */}
          <div className="mb-8">
            <ConferenceFilters
              selectedFilter={selectedFilter}
              onFilterChange={(value) =>
                availableFilters.includes(value)
                  ? setSelectedFilter(value)
                  : setSelectedFilter("all")
              }
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              customFilters={availableFilters}
            />
          </div>

          {/* Results */}
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="flex flex-col gap-4 items-center">
                <div className="relative">
                  <div className="w-8 h-8 rounded-full border-b-2 border-emerald-500 animate-spin"></div>
                  <div className="absolute inset-0 w-8 h-8 rounded-full border-2 border-emerald-200 animate-pulse"></div>
                </div>
                <p className="text-sm text-gray-600">Loading events...</p>
              </div>
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="flex justify-center items-center py-12">
              <div className="flex flex-col gap-4 items-center text-center">
                <div className="flex justify-center items-center w-16 h-16 bg-gray-100 rounded-full">
                  <svg
                    className="w-8 h-8 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    No events found
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    {searchQuery
                      ? "Try adjusting your search or filter criteria."
                      : "No events available at the moment."}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={`${selectedFilter}-${searchQuery}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.35 }}
                className="grid grid-cols-2 gap-3 sm:gap-4 lg:gap-6 sm:grid-cols-2 lg:grid-cols-4"
              >
                {filteredEvents.map((item, index) => {
                  // Normalize organizer name to a string to avoid rendering objects
                  const organizerName =
                    (typeof item.organizer_name === "string" &&
                      item.organizer_name.trim()) ||
                    (item.organizer_name &&
                      typeof item.organizer_name === "object" &&
                      item.organizer_name.name) ||
                    item.organizer?.name ||
                    (typeof item.organizer === "string" &&
                      item.organizer.trim()) ||
                    item.organizer_slug ||
                    null;

                  const displayItem = {
                    ...item,
                    organizer_name: organizerName,
                  };

                  return (
                  <motion.div
                    key={item.id || item._id || index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="w-full"
                  >
                    <ConferenceCard
                      item={displayItem}
                      onClick={() => {
                        if (displayItem.slug) {
                          navigate(`/event-details/${displayItem.slug}`);
                        }
                      }}
                    />
                  </motion.div>
                  );
                })}
              </motion.div>
            </AnimatePresence>
          )}

          {/* Results count */}
          {!loading && filteredEvents.length > 0 && (
            <div className="mt-6 text-sm text-gray-600">
              Showing {filteredEvents.length} of {events.length} results
            </div>
          )}
        </div>
      </section>

      {/* Why Attend Carousel Section */}
      {!isAuthenticated && <WhyAttendCarousel />}

      {/* Newsletter Subscription Section */}
      {!isAuthenticated && <NewsletterSubscription />}

      {/* Footer */}
      <Footer />
    </div>
  );
}
